
import {zip as observableZip,  Observable ,  Subscription } from 'rxjs';

import {map, filter} from 'rxjs/operators';
import { BanksAction } from '@toro/common/shared/state-management/banks/banks.action';
import { UsersState } from '@toro/common/shared/state-management/users/users.reducer';
import { DisposableSafelly } from '@toro/common/shared/components/disposable-safelly/disposable-safelly.component';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { InstitutionalAccountsState } from '@toro/common/shared/state-management/institutional-accounts/institutional-accounts.reducer';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { InstitutionalAccountsActions } from '@toro/common/shared/state-management/institutional-accounts/institutional-accounts.actions';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { UserProfileState } from '@toro/common/shared/state-management/userprofile/userprofile.reducer';
import { Router } from '@angular/router';
import { LoginChallengeResultType } from '@toro/common/shared/models/authentication/login.challenge.result.types';
import { LoginWraperService } from '@toro/common/shared/services/login.wraper.service';
import { formatCBLC } from './../../../../shared/utils';
import { environment } from '@hub-workspace/apps/logged-home/src/environments/environment';

@AutoUnsubscribe()
@Component({
    selector: 'institutional-transfer',
    templateUrl: './institutional-transfer.component.html',
    styleUrls: ['./institutional-transfer.component.scss'],
})
export class InstitutionalTransferComponent extends DisposableSafelly implements OnDestroy {
    public isNewBrand: boolean = environment.isNewBrand

    public BANK_ITAU_ID: number = 1;
    public institutionalAccounts: any[] = [];
    public userAuth: any;
    public user: any;

    private banks$: Observable<any>;
    private accountsSubscription$: Subscription;
    private institutionalAccounts$: Observable<InstitutionalAccountsState>;

    private authentication$: Observable<UsersState>;
    private user$: Observable<UserProfileState>;

    constructor(
        private store: Store<AppState>,
        private router: Router,
        private loginWraperService: LoginWraperService,
        ) {

        super();
        this.banks$ = this.store.select((state) => state.banks);
        this.institutionalAccounts$ = this.store.select((state) => state.institutionalAccounts);

        this.authentication$ = this.store.select((state) => state.authentication);
        this.user$ = this.store.select((state) => state.userProfile);
    }

    public ngOnInit() {
        this.authentication$.subscribe((auth) => {
            if (auth.currentUser === null ) return;
            this.userAuth = {...auth};
            this.userAuth.currentUser.cblc = formatCBLC(this.userAuth.currentUser.cblc);
        });

        this.user$.subscribe((user) => this.user = user );

        // Carregar lista de contas bancárias
        this.loadInstitutionalAccounts();

        this.store.dispatch({ type: BanksAction.getItems });
        this.store.dispatch({ type: InstitutionalAccountsActions.getItems });
    }

    /**
     * Mostra a conta de sua titularidade para o usuário no accordion
     */
    public showUserAccount() {
        if (this.user === undefined || this.userAuth === undefined) return false;
        return this.user.user.cpfcnpj && this.userAuth.currentUser.cblc;
    }

    /**
     * Mostra a conta de sua titularidade para os usuários com CBLC final 1 e 2
     */
    public showSelectedCBLC() {
        if (this.user === undefined || this.userAuth === undefined) return false;
        return this.userAuth.currentUser.cblc[this.userAuth.currentUser.cblc.length - 1] === '1' ||
            this.userAuth.currentUser.cblc[this.userAuth.currentUser.cblc.length - 1] === '2';
    }

    /**
     * Abre o painel de login
     */
    public showLoginPanel() {
        const snapshot = this.router.routerState.snapshot.url;
        this.loginWraperService.navigateToLogin({afterLoginRedirectTo: snapshot });
    }

    public ngOnDestroy() {
        // Não remover, necessário para o funcionamento correto do @AutoUnsubscribe
        if (this.accountsSubscription$) this.accountsSubscription$.unsubscribe();
    }

    /**
     * Carrega as contas bancárias da Toro fazendo a agregação com o banco relacionado
     */
    private loadInstitutionalAccounts() {
        // Incluir a lista de bancos e de contas da Toro
        this.accountsSubscription$ = observableZip(
                this.institutionalAccounts$,
                this.banks$,
            ).pipe(
            // Filtra os estados para saber se há tanto bancos quantos contas já carregadas
            filter(([accounts, banks]) => (
                accounts.data && banks.data && accounts.data.length && banks.data.length
            )),
            // Mapeia os retornos e forma um único array agregado de contas e bancos
            map(([accounts, banks]) => accounts.data.map((account) => ({
                ...account,
                bank: banks.data.find((bank) => bank.id === account.bankId),
            }))),
            // Aplica formatações em alguns campos
            map((accounts) => accounts.map((account) => {

                if (account.bank) {
                    // Definir zeros a esquerda nos códgos para que todos tenham 3 digitos
                    account.bank.code = this.formatBankCode(account.bank.code);

                    // Remover de números de agência (no momento apenas para bancos)
                    account.agency = account.bank.id === this.BANK_ITAU_ID ? this.formatAccountAgency(account.agency) : account.agency;
                }

                return account;

            })),)
            .subscribe((accounts) => this.institutionalAccounts = accounts);
    }

    /**
     * Formata o número da agência bancária forçando-a a ter 4 digítos
     * removendo zeros a esquerda
     *
     * @param {number} agencyNumber
     */
    private formatAccountAgency(agency: string|number) {

        const agencyStr = agency.toString();

        if (agencyStr.length > 4 && agencyStr[0] === '0') {
            return agencyStr.substr(1);
        }

        return agency.toString();
    }

    /**
     * Formata códgos de bancos no padrão de 3 digitos
     *
     * @param  {number|string} value
     * @returns string
     */
    private formatBankCode(code: number|string): string {

        // Converte em inteiro para verificação
        const strCode = parseInt(code.toString(), Infinity);

        // Adiciona 0 a esquerda se número não conter 3 digítos ou mais
        return strCode < 100 ? '0' + strCode : strCode.toString();
    }

    /**
     * Remove todos zeros a esquerda de uma string
     * @param value
     */
    private removeLandingZeros(value: string|number): string {
        return value
            .toString()
            .replace(/^0+/, '');
    }
}
